import { useEffect, useState } from 'react';
import Modal from './Modal';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import {
  subscribeAdminSettings,
  saveAdminSettings,
  DEFAULT_ADMIN_SETTINGS,
} from '../utils/posSubscriptionUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * Admin Settings — how clients pay US for their subscription.
 *
 * Everything here is shown on every client's checkout screen: the QR code
 * they scan, the name it should show once scanned (so they can tell they are
 * paying the right account), and the price of an extra staff account. The
 * landing page's checkout shows the same QR and account name, and quotes the
 * staff and warehouse add-on prices.
 *
 * @param {object} props
 * @param {string} props.signedInEmail - recorded on the admin audit trail
 * @returns {JSX.Element}
 */
function PosAdminSettingsForm({ signedInEmail }) {
  const { config } = useConfig();
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(DEFAULT_ADMIN_SETTINGS);
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState('');
  const [clearQr, setClearQr] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null);

  // Seeded once rather than kept live, so another administrator's save cannot
  // rewrite a field under the cursor.
  useEffect(() => {
    let seeded = false;
    return subscribeAdminSettings(
      (settings) => {
        if (seeded) return;
        seeded = true;
        setLoaded(settings);
        setForm({
          ...settings,
          extraStaffPrice: String(settings.extraStaffPrice),
          warehousePrice: String(settings.warehousePrice),
        });
      },
      (error) => { setLoadError(error); setLoaded(DEFAULT_ADMIN_SETTINGS); },
    );
  }, []);

  // Object URLs leak for the life of the page unless revoked.
  useEffect(() => () => {
    if (qrPreview.startsWith('blob:')) URL.revokeObjectURL(qrPreview);
  }, [qrPreview]);

  /**
   * @param {string} key
   * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const field = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  /**
   * Takes the chosen QR image and previews it. Compressed on save.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handlePickQr = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setQrFile(file);
    setClearQr(false);
    setQrPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await saveAdminSettings({ ...form, qrFile, clearQr }, loaded, signedInEmail);
      setLoaded(saved);
      setQrFile(null);
      setQrPreview('');
      setClearQr(false);
      setModal({ type: 'success', title: 'Admin settings saved', message: 'Clients will see these details on their next checkout.' });
    } catch (error) {
      console.error('Saving admin settings failed:', error);
      setModal({
        type: 'error',
        title: 'Could not save',
        message: error.isUserFacing ? error.message : describeWriteError(error, { what: 'the admin settings', path: 'posAdminSettings' }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <div className="pa-loading">{spinnerRing('Loading admin settings')}</div>;
  }

  const shownQr = clearQr ? '' : (qrPreview || loaded.paymentQrUrl);

  return (
    <form className="pa-form" onSubmit={handleSubmit}>
      {loadError && (
        <p className="pa-field-hint">The saved settings could not be read ({loadError.message}); the fields show the defaults.</p>
      )}
      <div className="pa-form-grid">
        <div className="pa-field pa-field-wide">
          <span className="pa-field-label">Payment QR code</span>
          <div className="ps-qr-row">
            <div className={`ps-qr-frame${shownQr ? '' : ' empty'}`}>
              {shownQr ? <img src={shownQr} alt="Payment QR code" /> : <span>No QR yet</span>}
            </div>
            <div className="ps-qr-actions">
              <label className="pa-btn pa-btn-quiet">
                {shownQr ? 'Replace QR' : 'Upload QR'}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handlePickQr} disabled={saving} hidden />
              </label>
              {shownQr && (
                <button
                  type="button"
                  className="pa-btn pa-btn-quiet"
                  onClick={() => { setQrFile(null); setQrPreview(''); setClearQr(true); }}
                  disabled={saving}
                >
                  Remove QR
                </button>
              )}
              <span className="pa-field-hint">
                Shown on every client&rsquo;s checkout screen (GCash, Maya or bank QR). Kept sharp
                and under 100KB so it still scans.
              </span>
            </div>
          </div>
        </div>

        <label className="pa-field">
          <span className="pa-field-label">Account name</span>
          <input className="pa-input" value={form.paymentAccountName} onChange={field('paymentAccountName')} placeholder="The Website Pal" disabled={saving} />
          <span className="pa-field-hint">What the client should see once they scan — so they know they are paying the right account.</span>
        </label>

        <label className="pa-field">
          <span className="pa-field-label">Account number</span>
          <input className="pa-input" value={form.paymentAccountNumber} onChange={field('paymentAccountNumber')} placeholder="0917 000 0000" disabled={saving} />
          <span className="pa-field-hint">Optional. For clients paying by transfer instead of scanning.</span>
        </label>

        <label className="pa-field">
          <span className="pa-field-label">Price per extra staff account</span>
          <input
            className="pa-input"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={form.extraStaffPrice}
            onChange={field('extraStaffPrice')}
            disabled={saving}
          />
          <span className="pa-field-hint">
            Charged once per account for the whole length of the plan it is bought with
            ({formatMoney(Number(form.extraStaffPrice) || 0, config)} each).
          </span>
        </label>

        <label className="pa-field">
          <span className="pa-field-label">Warehouse / Stock Room add-on price</span>
          <input
            className="pa-input"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={form.warehousePrice}
            onChange={field('warehousePrice')}
            disabled={saving}
          />
          <span className="pa-field-hint">
            Quoted on the landing page&rsquo;s pricing card and FAQ
            ({formatMoney(Number(form.warehousePrice) || 0, config)} a month).
          </span>
        </label>

        <label className="pa-field pa-field-wide">
          <span className="pa-field-label">Payment instructions</span>
          <textarea
            className="pa-input pa-textarea"
            rows={3}
            value={form.paymentInstructions}
            onChange={field('paymentInstructions')}
            placeholder="Pay the exact total, then upload a screenshot showing the reference number."
            disabled={saving}
          />
        </label>
      </div>

      <div className="pa-form-actions">
        <button type="submit" className="pa-btn pa-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save admin settings'}
        </button>
      </div>

      {saving && <div className="pa-saving">{spinnerRing('Saving')}</div>}

      <Modal
        isOpen={Boolean(modal)}
        onClose={() => setModal(null)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
      />
    </form>
  );
}

export default PosAdminSettingsForm;
